import { Injectable } from '@angular/core';
import { ActionCreatorAbstract } from '../../core/classes/action-creator.abstract';
import { IgenomicsState } from '../../core/interfaces/genomics-state.interface';
import {select, Store} from '@ngrx/store';
import { Observable } from 'rxjs/Observable';
import { ClearConfigurationAction, SetConfigurationAction, StartFetchingConfigurationAction } from '../store/configuration.actions';
import { IConfigurationsMap, IDefinition } from '../interfaces/definition.interface';
import { selectCurrentConfig, selectCurrentConfigId, selectConfigById } from '../store/configuration.selector';
import { filter, flatMap, map, take } from 'rxjs/operators';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { Resources } from '../../workflow-config/constants/resources.enum';
import { Workflow } from '../../core/interfaces/workflow.interface';
import { IWorkflowRequest } from '../../workflow-config/interfaces/workflow-request.interface';
import { get, cloneDeep, concat, uniqBy, isEmpty } from 'lodash';
import { IStepConfiguration } from '../interfaces/step-configuration.interface';
import {
  selectActiveWorkflow,
  selectActiveWorkflowActiveJobPurpose, selectResourceMap
} from "@ispm/workflow/store/workflow-config.selectors";
import {IResourcesMap} from "@ispm/workflow/interfaces/resource-map.interface";
@Injectable()
export class ConfigurationStoreHandler extends ActionCreatorAbstract {
  constructor(
    store: Store<IgenomicsState>,
  ) {
    super(store);
  }


  public loadConfiguration(id: string, type: 'test' = 'test'): void {
    this.dispatch(new StartFetchingConfigurationAction({ configurationId: id, configurationType: type}));
  }

  public setConfiguration(configuration: IDefinition): void {
    this.dispatch(new SetConfigurationAction(configuration));
  }

  public clearConfiguration(resourceType: string): void {
    this.dispatch(new ClearConfigurationAction(resourceType));
  }

  public getCurrentConfig(resourceType = 'Test'): Observable<IDefinition> {
    return this.store.pipe(select(selectCurrentConfig(resourceType)));
  }

  public getCurrentConfigId(resourceType): Observable<string> {
    return this.store.pipe(map(selectCurrentConfigId(resourceType)));

  }

  public getConfigById(resourceType, configId): Observable<IDefinition> {
    return this.store.pipe(map(selectConfigById(resourceType,configId)));
  }
  public getCurrentActiveWorkflow(): Observable<IWorkflowRequest> {
  let activeWorkflow$: Observable<IWorkflowRequest> = this.store.select(selectActiveWorkflow);
    return activeWorkflow$
      .pipe(
        filter(workflow => !!workflow),
        take(1)
      );
  }
  public getResourcesMap(): Observable<IResourcesMap> {
  let resourcesMap$: Observable<IResourcesMap> = this.store.select(selectResourceMap);
    return resourcesMap$.filter(resourcesMap => !!resourcesMap).take(1);
  }

  /**
   * returns formalize configuration object for current workflow step
   * calculation done by workflow stored value
   * @return {Observable<void>}
   */
   public getConfigurationForCurrentStep(resourceType: string = 'Test'): Observable<IStepConfiguration<IConfigurationsMap>> {
    return this.getCurrentConfig(resourceType).pipe(
      take(1),
      filter(config => !!config),
      flatMap(configuration => {
        return forkJoin([this.getCurrentActiveWorkflow(), this.getResourcesMap()])
        .pipe(
            map(res => {
              const currentWorkflow = res[0] as IWorkflowRequest;
              const currentRevisionId = res[1][Resources.RequestedRevision];
              const currentRevision = Workflow.getRevision(currentWorkflow.revisions, currentRevisionId);
              const activeJob = Workflow.getStepByJobId(currentRevision, currentWorkflow.activeJob);
              if (activeJob) {
                const stepConfiguration = cloneDeep(get(configuration.workflowSteps, activeJob.purpose, { })) as IStepConfiguration<any>;

                const defaultMap = get(configuration, `configurationsMap.default`, { });
                const destMap = get(configuration, `${stepConfiguration.fieldsValidation}`, { });
                stepConfiguration.fieldsValidation = this.mergeConfigs(defaultMap, destMap);
                return isEmpty(stepConfiguration.fieldsValidation)
                  ? null
                  : stepConfiguration as IStepConfiguration<IConfigurationsMap>;
              } else {
                return null;
              }

            })
          );
      })
    );
  }

  public getCurrentStepPurpose() {
    return this.store.select(selectActiveWorkflowActiveJobPurpose);
  }

  private mergeRequiredToProceed(base: IConfigurationsMap, overrideConfig: IConfigurationsMap) {
    const mergedConfig = cloneDeep(base);

    if (overrideConfig.requiredToProceed.diagnosticOrder) {
      if (Array.isArray(mergedConfig.requiredToProceed.diagnosticOrder)) {
        mergedConfig.requiredToProceed.diagnosticOrder = mergedConfig.requiredToProceed.diagnosticOrder.concat(
          overrideConfig.requiredToProceed.diagnosticOrder
        );
      } else {
        mergedConfig.requiredToProceed.diagnosticOrder = overrideConfig.requiredToProceed.diagnosticOrder;
      }
    }

    if (overrideConfig.requiredToProceed.specimen) {
      if (Array.isArray(mergedConfig.requiredToProceed.specimen)) {
        mergedConfig.requiredToProceed.specimen = mergedConfig.requiredToProceed.specimen.concat(
          overrideConfig.requiredToProceed.specimen
        );
      } else {
        mergedConfig.requiredToProceed.specimen = overrideConfig.requiredToProceed.specimen;
      }
    }

    return mergedConfig;
  }

  private mergeConfigs(base, overrideConfig) {
    let mergedConfig = cloneDeep(base);

    if (overrideConfig.requiredToProceed) {
      if (mergedConfig.requiredToProceed) {
        mergedConfig.requiredToProceed = this.mergeRequiredToProceed(
          mergedConfig.requiredToProceed,
          overrideConfig.requiredToProceed
        );
      } else {
        mergedConfig.requiredToProceed = overrideConfig.requiredToProceed;
      }
    }
    if (mergedConfig.isEditable !== undefined || overrideConfig.isEditable !== undefined) {
      mergedConfig.isEditable = overrideConfig.isEditable !== undefined
        ? overrideConfig.isEditable
        : mergedConfig.isEditable;
    }

    if (overrideConfig.override) {
      if (mergedConfig.override !== undefined && Array.isArray(mergedConfig.override)) {
        mergedConfig.override = this.mergeOverride(mergedConfig.override, overrideConfig.override);
      } else {
        mergedConfig.override = overrideConfig.override;
      }
    }

    return mergedConfig;
  }

  /**
   *
   * @param {[]} base
   * @param {[]} override
   */
  private mergeOverride(base: Array<any>, override: any) {
    const merged = concat(base, override);
    merged.reverse();
    const mergedUniq = uniqBy(merged, (item) => item.field + item.form);
    return mergedUniq;
  }


  private extractOverrideConfiguration(source, path) {
    return get(source, path);
  }

}
